// React
import React, { useEffect, useMemo, useState } from 'react';

// Assets
import { Bell, ChevronDown, LogOut, Repeat } from 'lucide-react';

// Components
import OrgList from './OrgList';

// Hooks
import { useNavigate } from 'react-router';
import { useSwitchApp } from 'hooks/useSwitchApp';

// Packages
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Box,
    ButtonBase,
    Divider,
    MenuItem,
    MenuList,
    Popover,
    Skeleton,
    Typography
} from '@mui/material';

// Redux - Actions, Reducers, Sagas
import { logoutUser } from 'store/actions/Auth';

// Utils
import { userRoles } from 'utils/constants';
import { getRoleLabel } from 'utils/role-label';
import { AuthHelper } from 'utils/helpers/auth.helper';
import capitalize from 'utils/capitalize';

const ellipsis = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
};

const dividerSx = { borderColor: '#f0f0f0', my: '0 !important' };

const MenuAction = ({ id, icon, label, onClick, disabled }) => (
    <MenuItem
        id={id}
        onClick={onClick}
        disabled={disabled}
        sx={{
            px: '20px',
            py: '10px',
            gap: '12px',
            '&:hover': { bgcolor: '#f9f9f9' }
        }}
    >
        <Box sx={{ display: 'flex', color: '#596470' }}>{icon}</Box>
        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#212322' }}>
            {label}
        </Typography>
    </MenuItem>
);

MenuAction.propTypes = {
    id: PropTypes.string,
    icon: PropTypes.node,
    label: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool
};

// The signed-in user at the top right of the operator app: who they are and their role in the
// active org. Its menu switches org (or app, for someone who is also a resident), opens the
// notification settings and logs out. Same visual language as the header buttons next to it and
// as the resident app's user menu (`components/Resi/Header/ResiUserMenu.jsx`).
const Profile = ({ userData, changingFocus, activeCalls, activeComs, logoutUser }) => {
    const navigate = useNavigate();
    const { canSwitch, label: switchAppLabel, switching, switchApp } = useSwitchApp();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const names = [userData?.first_name, userData?.last_name]
        .filter(Boolean)
        .map(capitalize);
    const name = names.join(' ');
    const initials = names
        .map(part => part[0])
        .join('')
        .toUpperCase();
    const roleLabel = useMemo(() => getRoleLabel(userData), [userData]);

    const userIsResiOrTenant =
        userData?.role === userRoles.RESIDENT || userData?.role === userRoles.TENANT;

    // On a call the user stays in the org the call belongs to.
    const onCall =
        Boolean(activeCalls?.some(call => call.fielded_by === userData?.uid)) ||
        Boolean(activeComs?.some(call => call.connected_by === userData?.uid));

    const handleClose = () => setAnchorEl(null);

    useEffect(() => {
        if (changingFocus) setAnchorEl(null);
    }, [changingFocus]);

    const handleSwitchApp = () => {
        handleClose();
        switchApp();
    };

    const handleNotificationSettings = () => {
        handleClose();
        navigate('/my-notifications');
    };

    const handleLogout = async () => {
        handleClose();
        await AuthHelper.logoutUser();
        logoutUser();
        navigate('/login', { state: { from: '' } });
    };

    // Until the user document arrives: the same shape as the button, so the header does not jump.
    if (!name) {
        return (
            <Box
                data-testid="profile-skeleton"
                aria-busy="true"
                sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    px: '6px',
                    py: '4px'
                }}
            >
                <Skeleton variant="circular" width={32} height={32} />
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Skeleton
                        variant="text"
                        width={120}
                        height={18}
                        sx={{ fontSize: '13px' }}
                    />
                    <Skeleton
                        variant="text"
                        width={80}
                        height={16}
                        sx={{ fontSize: '12px' }}
                    />
                </Box>
                {/* Where the chevron goes. */}
                <Box sx={{ width: 14, flexShrink: 0 }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexShrink: 0 }}>
            <ButtonBase
                id="header_user_button"
                aria-label="open profile"
                aria-haspopup="true"
                aria-controls={open ? 'profile-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                onClick={event => setAnchorEl(event.currentTarget)}
                sx={{
                    gap: '8px',
                    px: '6px',
                    py: '4px',
                    borderRadius: '8px',
                    textAlign: 'left',
                    bgcolor: open ? '#f8f8f8' : 'transparent',
                    transition: 'background-color 150ms ease',
                    '&:hover': { bgcolor: '#f8f8f8' },
                    '&.Mui-focusVisible': {
                        outline: '2px solid #33c78f',
                        outlineOffset: 2
                    }
                }}
            >
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: '#ecf1f3',
                        border: '0.762px solid #dedede',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}
                >
                    <Typography
                        sx={{ fontSize: '12px', fontWeight: 500, color: '#596470' }}
                    >
                        {initials}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        minWidth: 0,
                        maxWidth: 180
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            lineHeight: '18px',
                            color: '#212322',
                            ...ellipsis
                        }}
                    >
                        {name}
                    </Typography>
                    {/* The role they hold in the active org. */}
                    {roleLabel && (
                        <Typography
                            sx={{
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#8c8c8c',
                                ...ellipsis
                            }}
                        >
                            {roleLabel}
                        </Typography>
                    )}
                </Box>
                <ChevronDown
                    size={14}
                    color="#8c8c8c"
                    style={{
                        flexShrink: 0,
                        transition: 'transform 150ms ease',
                        transform: open ? 'rotate(180deg)' : 'none'
                    }}
                />
            </ButtonBase>

            <Popover
                id="profile-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            width: 300,
                            maxWidth: 'calc(100vw - 32px)',
                            maxHeight: 'calc(100vh - 96px)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            boxShadow: '0px 4px 8px 0px rgba(0,0,0,0.15)'
                        }
                    }
                }}
            >
                <Box sx={{ px: '20px', py: '12px', flexShrink: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#212322',
                            ...ellipsis
                        }}
                    >
                        {name}
                    </Typography>
                    {userData?.email && (
                        <Typography
                            sx={{
                                fontSize: '12px',
                                color: '#8c8c8c',
                                mt: '2px',
                                ...ellipsis
                            }}
                        >
                            {userData.email.toLowerCase()}
                        </Typography>
                    )}
                </Box>

                {canSwitch && (
                    <>
                        <Divider sx={dividerSx} />
                        <MenuList sx={{ flexShrink: 0, p: 0 }}>
                            <MenuAction
                                icon={<Repeat size={16} />}
                                label={switchAppLabel}
                                onClick={handleSwitchApp}
                                disabled={switching}
                            />
                        </MenuList>
                    </>
                )}

                {/* Residents and tenants have no orgs to switch between. */}
                {!userIsResiOrTenant && (
                    <>
                        <Divider sx={dividerSx} />
                        <OrgList locked={onCall} onSwitch={handleClose} />
                    </>
                )}

                <Divider sx={dividerSx} />
                <MenuList sx={{ flexShrink: 0, py: '4px' }}>
                    {!userIsResiOrTenant && (
                        <MenuAction
                            icon={<Bell size={16} />}
                            label="Notification settings"
                            onClick={handleNotificationSettings}
                        />
                    )}
                    <MenuAction
                        id="header_logout_button"
                        icon={<LogOut size={16} />}
                        label="Logout"
                        onClick={handleLogout}
                    />
                </MenuList>
            </Popover>
        </Box>
    );
};

Profile.propTypes = {
    userData: PropTypes.object,
    changingFocus: PropTypes.bool,
    activeCalls: PropTypes.array,
    activeComs: PropTypes.array,
    logoutUser: PropTypes.func
};

const mapStateToProps = ({ user, calls, coms }) => {
    const { userData, changingFocus } = user;
    const { active: activeCalls } = calls;
    const { active: activeComs } = coms;
    return { userData, changingFocus, activeCalls, activeComs };
};

export default connect(mapStateToProps, { logoutUser })(Profile);
